import {
  isLastXDays,
  isNextXDays,
  isLastXMonths,
  isNextXMonths,
  isLastXYears,
  isNextXYears,
  isLastXQuarters,
  isNextXQuarters,
  isDateOffsetGreaterThan,
  isDateOffsetLessThan,
  isDateOffsetEquals,
  isDateOffsetDoesNotEqual,
  isGreaterThan,
  isLessThan,
  isEquals,
  isDoesNotEqual,
  isBetween,
  isCurrentDay,
  isCurrentMonth,
  isCurrentQuarter,
  isCurrentYear,
  isAfterToday,
  isBeforeToday
} from './dateOperators';

export function evaluateFilterCriteria(rule, fieldIdValueMap, operatorType, conditionType, fieldIdsMap, invokeFieldsDetailsObject) {
  let result = false;
  const criteriaConditions = rule.filter_conditions;
  criteriaConditions.sort((a, b) => (a.condition_order > b.condition_order ? 1 : -1));
  let operatorLogic = '';
  if (criteriaConditions !== null && criteriaConditions.length > 0) {
    if (rule.operator_logic === null || rule.operator_logic === '') {
      // Build Default OpefilterCriteriarator Logic (conditions separated by AND operator) if the value is empty
      operatorLogic = buildDefaultOperatorLogic(criteriaConditions);
    } else {
      operatorLogic = rule.operator_logic;
    }

    // Evaluate each of the criteria condition based on different operator types supported for each of the fields
    // This has to run in reverse as the condition order value will be replaced in operatorLogic. If loop runs in forward direction then 11 will be replaced with truetrue when i=1
    // eslint-disable-next-line no-plusplus
    for (let i = criteriaConditions.length; i > 0; i--) {
      const hasField = fieldIdValueMap.has(criteriaConditions[i - 1].field_id);
      if (hasField) {
        const fieldValue = fieldIdValueMap.get(criteriaConditions[i - 1].field_id);
        const invokeFieldId = fieldIdsMap.get(criteriaConditions[i - 1].field_id);
        const conditionResult = evaluateCriteriaCondition(
          criteriaConditions[i - 1],
          fieldValue,
          conditionType,
          operatorType,
          invokeFieldId,
          invokeFieldsDetailsObject
        );
        operatorLogic = operatorLogic.replace(i, conditionResult.toString());
      } else {
        operatorLogic = operatorLogic.replace(i, 'false');
      }
    }
    // console.log(`Operator Logic before final evaluation: ${operatorLogic}`);
    // Replace Final Boolean Operators with what javascript eval function supports
    operatorLogic = operatorLogic
      .replace(/or/gi, '||')
      .replace(/and/gi, '&&')
      .replace(/not/gi, '!');

    // if there's any additional non-replaced numbers, replace them with false (can be a situation when field is deleted or is made inactive?)
    operatorLogic = operatorLogic.replace(/(\d+)+/g, 'false');
    try {
      // console.log(`Final Operator Logic:${operatorLogic}`);
      // eslint-disable-next-line
      result = eval(operatorLogic);
    } catch (error) {
      // console.error(`Rule:${rule.id} Rule Name:${rule.name} contains inavlid operator logic`);
      // console.error(error);
      result = false;
    }
  }
  return result;
}

function decodeEntities(encodedString) {
  const translate_re = /&(nbsp|amp|quot|lt|gt|&#39);/g;
  const translate = {
    nbsp: ' ',
    amp: '&',
    quot: '"',
    lt: '<',
    gt: '>',
    '&#39': "'"
  };
  if (encodedString) {
    return (
      encodedString
        // eslint-disable-next-line func-names
        .replace(translate_re, function(match, entity) {
          return translate[entity];
        })
        // eslint-disable-next-line func-names
        .replace(/&#(\d+);/gi, function(match, numStr) {
          const num = parseInt(numStr, 10);
          return String.fromCharCode(num);
        })
    );
  }
  return encodedString;
}

function buildDefaultOperatorLogic(conditions) {
  let defaultOperatorLogic = '';
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < conditions.length; i++) {
    if (defaultOperatorLogic.length !== 0) {
      defaultOperatorLogic += ' AND ';
    }

    defaultOperatorLogic += (i + 1).toString();
  }
  return defaultOperatorLogic;
}

function evaluateCriteriaCondition(condition, value, conditionType, operatorType, portalFieldId, invokeFieldsDetailsObject) {
  let conditionResult = false;
  switch (condition.condition_type) {
    case conditionType.Text:
      conditionResult = evaluateTextCriteriaCondition(
        condition.operator_type,
        operatorType,
        condition.text_value,
        value,
        condition.condition_type,
        condition.requires_content_changed,
        condition.is_old_content,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;

    case conditionType.ValueList:
      conditionResult = evaluateValuesListCriteriaCondition(
        condition.operator_type,
        operatorType,
        condition.select_values,
        value,
        condition.condition_type,
        condition.requires_content_changed,
        condition.is_old_content,
        portalFieldId,
        invokeFieldsDetailsObject,
        condition.is_no_selection_included
      );
      break;

    case conditionType.Numeric:
      conditionResult = evaluateNumericCriteriaCondition(
        condition.operator_type,
        operatorType,
        condition.numeric_value,
        value,
        condition.condition_type,
        condition.requires_content_changed,
        condition.is_old_content,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;

    case conditionType.NumericBetween:
      conditionResult = evaluateNumericBetweenCriteriaCondition(condition.begin_value, condition.end_value, value);
      break;

    case conditionType.DateOffset:
      conditionResult = evaluateDateOffsetCriteriaCondition(condition.operator_type, operatorType, condition.date_offset, value);
      break;

    case conditionType.DateComparison:
      conditionResult = evaluateDateComparisonCriteriaCondition(
        condition.operator_type,
        operatorType,
        condition.date_value,
        value,
        condition.condition_type,
        condition.requires_content_changed,
        condition.is_old_content,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;

    case conditionType.DateRange:
      conditionResult = evaluateDateRangeCriteriaCondition(condition.operator_type, operatorType, condition, value);
      break;

    case conditionType.DateTimeRange:
      // returning false since time is not being handled
      conditionResult = false; // evaluateDateTimeRangeCriteriaCondition(condition.operator_type, operatorType, condition, value);
      break;

    case conditionType.CurrentDate:
      conditionResult = evaluateCurrentDateCriteriaCondition(condition.operator_type, operatorType, value);
      break;

    case conditionType.ChangedField:
      conditionResult = evaluateChangedFieldCriteriaCondition(invokeFieldsDetailsObject, portalFieldId);
      break;
    // TODO: Add more evaluation conditions here for Numeric, Date and Valueslist.
    // Default made true for now. When functionality is completed it should be false.
    default:
      conditionResult = false;
      break;
  }
  return conditionResult;
}

function evaluateTextCriteriaCondition(
  textOperatorType,
  operatorType,
  conditionValue,
  textValue,
  conditionType,
  requiresContentChanged,
  isOldContent,
  portalFieldId,
  invokeFieldsDetailsObject
) {
  let conditionResult = false;
  const textValueLowerCase = decodeEntities(textValue && textValue !== '' ? textValue.toLowerCase() : null);
  const conditionValueLowerCase = decodeEntities(conditionValue && conditionValue !== '' ? conditionValue.toLowerCase() : null);

  switch (textOperatorType) {
    case operatorType.Equals:
      conditionResult = evaluateChangedOperatorsCriteriaCondition(
        conditionType,
        conditionValueLowerCase,
        textValueLowerCase,
        requiresContentChanged,
        isOldContent,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;
    case operatorType.DoesNotEqual:
      conditionResult = textValueLowerCase !== conditionValueLowerCase;
      break;
    case operatorType.Contains:
      if (textValueLowerCase != null) {
        conditionResult = textValueLowerCase.indexOf(conditionValueLowerCase) >= 0;
      } else {
        conditionResult = textValueLowerCase === conditionValueLowerCase;
      }
      break;
    case operatorType.DoesNotContain:
      if (textValueLowerCase != null) {
        conditionResult = textValueLowerCase.indexOf(conditionValueLowerCase) < 0;
      } else {
        conditionResult = textValueLowerCase !== conditionValueLowerCase;
      }
      break;
    case operatorType.Exact:
      conditionResult = decodeEntities(textValue) === decodeEntities(conditionValue);
      break;
    default:
      break;
  }
  return conditionResult;
}

function evaluateValuesListCriteriaCondition(
  valuesListOperatorType,
  operatorType,
  conditionValues,
  fieldValue,
  conditionType,
  requiresContentChanged,
  isOldContent,
  portalFieldId,
  invokeFieldsDetailsObject,
  noSelection
) {
  // console.log(valuesListOperatorType);
  // console.log(`conditionValues: ${conditionValues}`);
  // console.log(`fieldValue : ${fieldValue}`);
  // console.log(`noSelection: ${noSelection}`);

  let conditionResult = false;

  switch (valuesListOperatorType) {
    case operatorType.Equals:
      conditionResult = evaluateChangedOperatorsCriteriaCondition(
        conditionType,
        conditionValues,
        fieldValue,
        requiresContentChanged,
        isOldContent,
        portalFieldId,
        invokeFieldsDetailsObject,
        noSelection
      );
      break;
    case operatorType.DoesNotEqual: {
      // Invalid Rule. Can't have options selected with No Selection
      if (noSelection && !IsValuesListNull(conditionValues)) {
        conditionResult = false;
      } else if (noSelection && IsValuesListNull(conditionValues) && IsValuesListNull(fieldValue)) {
        conditionResult = false;
      } else conditionResult = !(JSON.stringify(fieldValue.sort()) === JSON.stringify(conditionValues.sort()));

      break;
    }

    case operatorType.Contains: {
      conditionResult = doesValuesListContainValue(fieldValue, conditionValues, noSelection);
      break;
    }
    case operatorType.DoesNotContain: {
      conditionResult = !doesValuesListContainValue(fieldValue, conditionValues, noSelection);
      break;
    }
    default:
      break;
  }
  // console.log(conditionResult);
  return conditionResult;
}

function IsValuesListNull(fieldValue) {
  // eslint-disable-next-line react/destructuring-assignment
  return fieldValue === null || fieldValue.length === 0 || fieldValue === '[]' || (fieldValue.length === 1 && fieldValue[0] === null);
}

function doesValuesListContainValue(fieldValue, conditionValues, noSelection) {
  let containsValue = false;
  let containsNoSelection = false;
  if (conditionValues != null && fieldValue != null) {
    containsValue = conditionValues.some(v => fieldValue.includes(v));
  }
  if (noSelection) {
    containsNoSelection = IsValuesListNull(fieldValue);
  }
  return containsValue || containsNoSelection;
}

function evaluateNumericCriteriaCondition(
  numericOperatorType,
  operatorType,
  conditionValue,
  fieldValue,
  conditionType,
  requiresContentChanged,
  isOldContent,
  portalFieldId,
  invokeFieldsDetailsObject
) {
  let conditionResult = false;
  // eslint-disable-next-line no-param-reassign
  fieldValue = fieldValue === null ? undefined : fieldValue;
  // eslint-disable-next-line no-param-reassign
  conditionValue = conditionValue === null ? undefined : conditionValue;

  switch (numericOperatorType) {
    case operatorType.GreaterThan:
      conditionResult = fieldValue > conditionValue;
      break;

    case operatorType.LessThan:
      conditionResult = fieldValue < conditionValue;
      break;
    case operatorType.Equals:
      conditionResult = evaluateChangedOperatorsCriteriaCondition(
        conditionType,
        conditionValue,
        fieldValue,
        requiresContentChanged,
        isOldContent,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;

    case operatorType.NotEqual:
      // Type check not required here, only value check
      // eslint-disable-next-line
      conditionResult = fieldValue != conditionValue;
      break;
    default:
      conditionResult = false;
  }

  // console.log(conditionResult);
  return conditionResult;
}

function evaluateNumericBetweenCriteriaCondition(beginValue, endValue, fieldValue) {
  let conditionResult = false;

  if (fieldValue && beginValue && endValue) {
    conditionResult = fieldValue >= beginValue && fieldValue <= endValue;
  }

  // console.log(conditionResult);
  return conditionResult;
}

function evaluateDateOffsetCriteriaCondition(dateOffsetOperatorType, operatorType, offset, dateValue) {
  let conditionResult = false;
  const currentDate = new Date();

  if (dateValue) {
    switch (dateOffsetOperatorType) {
      case operatorType.LastXDays:
        conditionResult = isLastXDays(currentDate, offset, dateValue);
        break;
      case operatorType.NextXDays:
        conditionResult = isNextXDays(currentDate, offset, dateValue);
        break;
      case operatorType.LastXMonths:
        conditionResult = isLastXMonths(currentDate, offset, dateValue);
        break;
      case operatorType.NextXMonths:
        conditionResult = isNextXMonths(currentDate, offset, dateValue);
        break;
      case operatorType.LastXYears:
        conditionResult = isLastXYears(currentDate, offset, dateValue);
        break;
      case operatorType.NextXYears:
        conditionResult = isNextXYears(currentDate, offset, dateValue);
        break;
      case operatorType.LastXQuarters:
        conditionResult = isLastXQuarters(currentDate, offset, dateValue);
        break;
      case operatorType.NextXQuarters:
        conditionResult = isNextXQuarters(currentDate, offset, dateValue);
        break;
      case operatorType.LastXHours:
        conditionResult = false; // returning false since time is not being handled
        break;
      case operatorType.NextXHours:
        conditionResult = false; // returning false since time is not being handled
        break;
      case operatorType.LastXMinutes:
        conditionResult = false; // returning false since time is not being handled
        break;
      case operatorType.NextXMinutes:
        conditionResult = false; // returning false since time is not being handled
        break;
      case operatorType.GreaterThan:
        conditionResult = isDateOffsetGreaterThan(currentDate, offset, dateValue);
        break;
      case operatorType.LessThan:
        conditionResult = isDateOffsetLessThan(currentDate, offset, dateValue);
        break;
      case operatorType.Equals:
        conditionResult = isDateOffsetEquals(currentDate, offset, dateValue);
        break;
      case operatorType.DoesNotEqual:
        conditionResult = isDateOffsetDoesNotEqual(currentDate, offset, dateValue);
        break;
      default:
        conditionResult = false;
        break;
    }
  }
  // console.log(conditionResult);
  return conditionResult;
}

function evaluateDateComparisonCriteriaCondition(
  dateComparisonOperatorType,
  operatorType,
  conditionValue,
  dateValue,
  conditionType,
  requiresContentChanged,
  isOldContent,
  portalFieldId,
  invokeFieldsDetailsObject
) {
  let conditionResult = false;
  const fieldDateValue = new Date(dateValue).setUTCHours(0, 0, 0, 0);
  const conditionDateValue = new Date(conditionValue).setUTCHours(0, 0, 0, 0);
  switch (dateComparisonOperatorType) {
    case operatorType.GreaterThan:
      conditionResult = dateValue && conditionValue ? isGreaterThan(conditionDateValue, fieldDateValue) : false;
      break;
    case operatorType.LessThan:
      conditionResult = dateValue && conditionValue ? isLessThan(conditionDateValue, fieldDateValue) : false;
      break;
    case operatorType.Equals:
      conditionResult = evaluateChangedOperatorsCriteriaCondition(
        conditionType,
        conditionValue,
        dateValue,
        requiresContentChanged,
        isOldContent,
        portalFieldId,
        invokeFieldsDetailsObject
      );
      break;
    case operatorType.DoesNotEqual:
      if ((conditionValue === null || dateValue === null) && conditionValue !== dateValue) {
        conditionResult = true;
      } else if (conditionValue && dateValue) {
        conditionResult = isDoesNotEqual(conditionDateValue, fieldDateValue);
      }
      break;
    default:
      conditionResult = false;
      break;
  }
  // console.log(conditionResult);
  return conditionResult;
}

function evaluateDateRangeCriteriaCondition(dateRangeOperatorType, operatorType, condition, dateValue) {
  let conditionResult = false;

  const beginDate = condition.begin_date;
  const endDate = condition.end_date;

  if (dateValue && beginDate && endDate) {
    switch (dateRangeOperatorType) {
      case operatorType.Between:
        conditionResult = isBetween(beginDate, endDate, dateValue);
        break;
      default:
        conditionResult = false;
        break;
    }
  }

  // console.log(conditionResult);
  return conditionResult;
}

function evaluateCurrentDateCriteriaCondition(dateOperatorType, operatorType, conditionDateValue) {
  let conditionResult = false;

  if (conditionDateValue) {
    switch (dateOperatorType) {
      case operatorType.CurrentDay:
        conditionResult = isCurrentDay(conditionDateValue);
        break;
      case operatorType.CurrentMonth:
        conditionResult = isCurrentMonth(conditionDateValue);
        break;
      case operatorType.CurrentQuarter:
        conditionResult = isCurrentQuarter(conditionDateValue);
        break;
      case operatorType.CurrentYear:
        conditionResult = isCurrentYear(conditionDateValue);
        break;
      case operatorType.AfterToday:
        conditionResult = isAfterToday(conditionDateValue);
        break;
      case operatorType.PriorToToday:
        conditionResult = isBeforeToday(conditionDateValue);
        break;
      case operatorType.CurrentHour:
        // returning false since time is not being handled
        conditionResult = false; // isCurrentHour(conditionDateValue);
        break;
      case operatorType.CurrentMinute:
        // returning false since time is not being handled
        conditionResult = false; // isCurrentMinute(conditionDateValue);
        break;
      default:
        conditionResult = false;
        break;
    }
  }

  // console.log(conditionResult);
  return conditionResult;
}

function evaluateChangedOperatorsCriteriaCondition(
  conditionType,
  conditionValue,
  fieldValue,
  requiresContentChanged,
  isOldContent,
  portalFieldId,
  invokeFieldsDetailsObject,
  noSelection
) {
  let conditionResult = false;
  switch (conditionType) {
    case 'Text':
      if (requiresContentChanged) {
        if (invokeFieldsDetailsObject !== null && invokeFieldsDetailsObject.fieldID === portalFieldId) {
          if (isOldContent) {
            const oldValueLowerCase = decodeEntities(
              invokeFieldsDetailsObject.oldValue && invokeFieldsDetailsObject.oldValue !== ''
                ? invokeFieldsDetailsObject.oldValue.toLowerCase()
                : null
            );
            // ChangedFrom Condition
            conditionResult = conditionValue === oldValueLowerCase;
            // console.log('CONDITIONRESULTFROMCOMMONMETHOD', conditionResult);
          } else {
            // ChangedTo
            const newValueLowerCase = decodeEntities(
              invokeFieldsDetailsObject.newValue && invokeFieldsDetailsObject.newValue !== ''
                ? invokeFieldsDetailsObject.newValue.toLowerCase()
                : null
            );
            conditionResult = conditionValue === newValueLowerCase;
          }
        } else conditionResult = false;
      }
      // Equals Condition
      else {
        conditionResult = fieldValue === conditionValue;
      }
      break;
    case 'Numeric':
      if (requiresContentChanged) {
        if (invokeFieldsDetailsObject !== null && portalFieldId === invokeFieldsDetailsObject.fieldID) {
          if (isOldContent) {
            // eslint-disable-next-line
            conditionResult = conditionValue == invokeFieldsDetailsObject.oldValue;
          } else {
            // eslint-disable-next-line
            conditionResult = conditionValue == invokeFieldsDetailsObject.newValue;
          }
        } else {
          conditionResult = false;
        }
      } else {
        // eslint-disable-next-line
        conditionResult = fieldValue == conditionValue;
      }
      break;

    case 'ValueList':
      if (requiresContentChanged) {
        if (invokeFieldsDetailsObject !== null && invokeFieldsDetailsObject.fieldID === portalFieldId) {
          if (isOldContent) {
            /* ChangedFrom condition*/
            if (noSelection && !IsValuesListNull(conditionValue)) {
              conditionResult = false;
            } else if (noSelection && IsValuesListNull(conditionValue) && IsValuesListNull(invokeFieldsDetailsObject.oldValue)) {
              conditionResult = true;
            } else conditionResult = JSON.stringify(conditionValue) === JSON.stringify(invokeFieldsDetailsObject.oldValue);
          } else {
            /* ChangedTo condition*/
            // eslint-disable-next-line no-lonely-if
            if (noSelection && !IsValuesListNull(conditionValue)) {
              conditionResult = false;
            } else if (noSelection && IsValuesListNull(conditionValue) && IsValuesListNull(invokeFieldsDetailsObject.newValue)) {
              conditionResult = false;
            } else conditionResult = JSON.stringify(conditionValue) === JSON.stringify(invokeFieldsDetailsObject.newValue);
          }
        } else {
          conditionResult = false;
        }
      } else {
        // Equals Condition
        // Invalid Rule. Can't have options selected with No Selection
        // eslint-disable-next-line no-lonely-if
        if (noSelection && !IsValuesListNull(conditionValue)) {
          conditionResult = false;
        } else if (noSelection && IsValuesListNull(conditionValue) && IsValuesListNull(fieldValue)) {
          conditionResult = true;
        } else conditionResult = JSON.stringify(fieldValue.sort()) === JSON.stringify(conditionValue.sort());
      }
      break;
    case 'DateComparison':
      if (requiresContentChanged) {
        if (invokeFieldsDetailsObject !== null && invokeFieldsDetailsObject.fieldID === portalFieldId) {
          if (isOldContent) {
            /* ChangedFrom operator */
            conditionResult = evaluateDateEqualsCondition(conditionValue, invokeFieldsDetailsObject.oldValue);
          } else {
            /* ChangedTo operator */
            conditionResult = evaluateDateEqualsCondition(conditionValue, invokeFieldsDetailsObject.newValue);
          }
        } else conditionResult = false;
      } else {
        /* Equals*/
        conditionResult = evaluateDateEqualsCondition(conditionValue, fieldValue);
      }
      break;
    default:
      conditionResult = false;
      break;
  }
  return conditionResult;
}

function evaluateChangedFieldCriteriaCondition(invokeFieldsDetailsObject, portalfieldId) {
  let conditionResult = 'false';
  if (invokeFieldsDetailsObject != null) {
    switch (invokeFieldsDetailsObject.fieldType) {
      case 'TEXT':
        if (invokeFieldsDetailsObject.fieldID === portalfieldId) {
          conditionResult = invokeFieldsDetailsObject.oldValue !== invokeFieldsDetailsObject.newValue;
        }
        break;

      case 'NUMERIC':
        if (invokeFieldsDetailsObject.fieldID === portalfieldId) {
          conditionResult = invokeFieldsDetailsObject.oldValue !== invokeFieldsDetailsObject.newValue;
        }
        break;

      case 'DATE':
        if (invokeFieldsDetailsObject.fieldID === portalfieldId) {
          conditionResult = invokeFieldsDetailsObject.oldValue !== invokeFieldsDetailsObject.newValue;
          // console.log('CONDITIONRESULTFROMDATE', conditionResult);
        }
        break;

      case 'VALUES_LIST':
        if (invokeFieldsDetailsObject.fieldID === portalfieldId) {
          conditionResult = JSON.stringify(invokeFieldsDetailsObject.oldValue) !== JSON.stringify(invokeFieldsDetailsObject.newValue);
        }
        break;
      default:
        conditionResult = false;
        break;
    }
  } else {
    conditionResult = false;
  }
  return conditionResult;
}

function evaluateDateEqualsCondition(conditionValue, inputValue) {
  let result = false;
  if (conditionValue === null && inputValue === null) {
    result = true;
  } else if (conditionValue && inputValue) {
    const dateValue = new Date(inputValue).setUTCHours(0, 0, 0, 0);
    const conditiondateValue = new Date(conditionValue).setUTCHours(0, 0, 0, 0);
    result = isEquals(conditiondateValue, dateValue);
  }
  return result;
}

export default evaluateFilterCriteria;
